import {t} from 'i18next';

const ROLE_LABEL_KEYS = {
  exec_sponsor: 'workstream_role_label_exec_sponsor',
  dri: 'workstream_role_label_dri',
  reporter: 'workstream_role_label_reporter',
  steam_owner: 'workstream_role_label_steam_owner',
  working_group: 'workstream_role_label_working_group',
} as const;

const createWorkstreamRole = <RoleId extends keyof typeof ROLE_LABEL_KEYS>(
  id: RoleId,
  {
    required,
    order,
    show_by_default,
  }: {
    required: boolean;
    order: number;
    show_by_default: boolean;
  }
) => ({
  id,
  get label() {
    return t(ROLE_LABEL_KEYS[id]);
  },
  required,
  order,
  show_by_default,
});

export const WORKSTREAM_ROLES = {
  exec_sponsor: createWorkstreamRole('exec_sponsor', {
    required: false,
    order: 2,
    show_by_default: false,
  }),
  dri: createWorkstreamRole('dri', {
    required: true,
    order: 1,
    show_by_default: true,
  }),
  reporter: createWorkstreamRole('reporter', {
    required: false,
    order: 3,
    show_by_default: true,
  }),
  // show_by_default must be false for all roles below
  steam_owner: createWorkstreamRole('steam_owner', {
    required: false,
    order: 4,
    show_by_default: true,
  }),
  working_group: createWorkstreamRole('working_group', {
    required: false,
    order: 5,
    show_by_default: true,
  }),
} as const;

export type WorkstreamRoleId = keyof typeof WORKSTREAM_ROLES;

export type WorkstreamRole = (typeof WORKSTREAM_ROLES)[WorkstreamRoleId];

export const getWorkstreamRoles = (): WorkstreamRole[] =>
  Object.values(WORKSTREAM_ROLES).sort((a, b) => a.order - b.order);

export const getRequiredRoles = (): WorkstreamRole[] =>
  getWorkstreamRoles().filter((role) => role.required);

const getKnownRoleLabel = (roleId: WorkstreamRoleId): string => t(ROLE_LABEL_KEYS[roleId]);

export const getRoleLabel = (roleId: string): string =>
  roleId in WORKSTREAM_ROLES ? getKnownRoleLabel(roleId as WorkstreamRoleId) : roleId;

export const isValidRole = (roleId: string): roleId is WorkstreamRoleId =>
  roleId in WORKSTREAM_ROLES;
